import React, { useState, useEffect, useRef } from 'react';
import {
  Box, Card, CardContent, Typography, LinearProgress,
  Chip, IconButton, Avatar, Button, Tooltip, Divider,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  ToggleButton, ToggleButtonGroup,
  Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, Select, MenuItem, FormControl, InputLabel,
} from '@mui/material';
import {
  Assignment as TaskIcon,
  ViewKanban as KanbanIcon,
  TableRows as ListIcon,
  Add as AddIcon,
  CalendarToday as CalIcon,
  PersonOutline as PersonIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  CheckCircleOutline as CheckOutlineIcon,
  PlayArrow as PlayIcon,
  Stop as StopIcon,
  EditNote as EditNoteIcon,
  Close as CloseIcon,
  ArrowForward as ArrowForwardIcon,
} from '@mui/icons-material';
import { useHistory } from 'react-router-dom';
import apiService from '../../services/apiServices';
import globals from '../../services/globals';
import Toast from '../actions/Toasts';
import { useTranslation } from 'react-i18next';
import useConfirm from '../../hooks/useConfirm';

/* ─── constants ─────────────────────────────────────────────── */
const STATUS_COLS = [
  { key: 'pending',     label: "Për T'u Bërë", icon: '☐', color: '#b45309', bg: '#fefce8', border: '#fde68a' },
  { key: 'in_progress', label: 'Në Proces',    icon: '⚡', color: '#1d6b8a', bg: '#eff8fc', border: '#bae6fd' },
  { key: 'on_hold',     label: 'E Pezulluar',  icon: '⏸', color: '#92400e', bg: '#fff7ed', border: '#fed7aa' },
  { key: 'completed',   label: 'E Përfunduar', icon: '✔', color: '#166534', bg: '#f0fdf4', border: '#bbf7d0' },
];

const STATUS_LABELS   = { pending: "Për T'u Bërë", in_progress: 'Në Proces', on_hold: 'E Pezulluar', completed: 'E Përfunduar', cancelled: 'E Anuluar' };
const STATUS_COLORS   = { pending: 'warning', in_progress: 'info', on_hold: 'warning', completed: 'success', cancelled: 'default' };
const PRIORITY_LABELS = { low: 'E Ulët', medium: 'Mesatare', high: 'E Lartë', urgent: 'Urgjente' };
const PRIORITY_COLORS = { low: 'default', medium: 'primary', high: 'warning', urgent: 'error' };

const fmtDate = (d) =>
  d ? new Date(d).toLocaleDateString('sq-AL', { day: '2-digit', month: 'short' }) : '—';

const isTaskOverdue = (t) =>
  t.status !== 'completed' && t.status !== 'cancelled' && t.due_date && new Date(t.due_date) < new Date();

const toDatetimeLocal = (v) => {
  if (!v) return '';
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return '';
  return new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
};

const fmtElapsed = (sec) => {
  const s = Math.floor(sec);
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const ss = s % 60;
  const pad = (n) => String(n).padStart(2, '0');
  return h > 0 ? `${h}:${pad(m)}:${pad(ss)}` : `${pad(m)}:${pad(ss)}`;
};

const fmtHHMM = (date) => {
  if (!date) return '';
  return new Intl.DateTimeFormat('sq-AL', { hour: '2-digit', minute: '2-digit' }).format(date);
};

const nowLocal = () => {
  const d = new Date();
  return new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
};
const now10 = () => {
  const d = new Date(Date.now() + 10 * 60000);
  return new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
};

const EMPTY_FORM = {
  title: '', description: '', status: 'pending', priority: 'medium',
  assigned_to: '', client_id: '', project_id: '', activity_id: '',
  planned_start: '', planned_end: '',
};

/* ─── professional timer timeline bar ───────────────────────── */
function TimerTimeline({ timer, task, onStop }) {
  const { t } = useTranslation();
  if (!timer || !task) return null;
  const MAX_SEC = 90 * 60;
  const pct = Math.min((timer.elapsed / MAX_SEC) * 100, 100);
  const hue = Math.max(140 - Math.floor((timer.elapsed / MAX_SEC) * 80), 60);
  const barColor = `hsl(${hue}, 72%, 42%)`;
  const bgColor  = `hsl(${hue}, 72%, 96%)`;
  const segments = [15, 30, 45, 60, 75, 90];

  return (
    <Box sx={{
      mx: { xs: 0, md: 2 }, my: 1, borderRadius: 2.5,
      bgcolor: bgColor,
      border: `1.5px solid hsl(${hue},60%,82%)`,
      px: { xs: 1.5, sm: 2.5 }, py: 1.5,
      display: 'flex', flexDirection: 'column', gap: 1,
      boxShadow: `0 2px 12px hsl(${hue},60%,88%)`,
      animation: 'timerFadeIn .3s ease',
      '@keyframes timerFadeIn': { from: { opacity: 0, transform: 'translateY(-6px)' }, to: { opacity: 1, transform: 'none' } },
    }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
        <Box sx={{
          width: 10, height: 10, borderRadius: '50%', bgcolor: barColor, flexShrink: 0,
          boxShadow: `0 0 0 0 ${barColor}55`,
          animation: 'ripple 1.4s ease-out infinite',
          '@keyframes ripple': {
            '0%':   { boxShadow: `0 0 0 0 ${barColor}66` },
            '70%':  { boxShadow: `0 0 0 8px ${barColor}00` },
            '100%': { boxShadow: `0 0 0 0 ${barColor}00` },
          },
        }} />
        <Typography variant="caption" sx={{ fontWeight: 700, color: barColor, fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '.06em', flexShrink: 0 }}>
          {t('tasks.taskInProgress')}
        </Typography>
        <Typography variant="body2" sx={{ fontWeight: 700, flex: 1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', color: 'text.primary' }}>
          {task.title}
        </Typography>
        <Typography sx={{
          fontWeight: 800, fontSize: { xs: '1.1rem', sm: '1.4rem' },
          fontVariantNumeric: 'tabular-nums', color: barColor, flexShrink: 0,
          fontFamily: 'monospace',
        }}>
          {fmtElapsed(timer.elapsed)}
        </Typography>
        <Box component="span" sx={{ fontSize: '0.72rem', color: 'text.secondary', whiteSpace: 'nowrap', flexShrink: 0 }}>
          {t('tasks.startedAt', { time: fmtHHMM(timer.startedAt) })}
        </Box>
        <IconButton size="small" onClick={() => onStop(task)}
          sx={{
            bgcolor: barColor, color: '#fff', borderRadius: 1.5, p: '5px',
            '&:hover': { bgcolor: `hsl(${hue},72%,35%)` },
            flexShrink: 0,
          }}>
          <StopIcon sx={{ fontSize: 15 }} />
        </IconButton>
      </Box>
      <Box sx={{ position: 'relative' }}>
        <Box sx={{ position: 'absolute', inset: 0, display: 'flex', pointerEvents: 'none' }}>
          {segments.map((s) => (
            <Box key={s} sx={{
              position: 'absolute',
              left: `${(s / 90) * 100}%`,
              top: 0, bottom: 0,
              width: '1px',
              bgcolor: `hsl(${hue},40%,75%)`,
              opacity: 0.6,
            }} />
          ))}
        </Box>
        <Box sx={{ height: 8, borderRadius: 4, bgcolor: `hsl(${hue},50%,88%)`, overflow: 'hidden' }}>
          <Box sx={{
            height: '100%',
            width: `${pct}%`,
            borderRadius: 4,
            background: `linear-gradient(90deg, hsl(${hue},72%,52%), hsl(${hue},72%,38%))`,
            transition: 'width .9s linear',
          }} />
        </Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.25 }}>
          {segments.map((s) => (
            <Typography key={s} variant="caption" sx={{ fontSize: '0.58rem', color: 'text.disabled', minWidth: 20, textAlign: 'center' }}>{s}m</Typography>
          ))}
        </Box>
      </Box>
    </Box>
  );
}

/* ─── kanban task card ───────────────────────────────────────── */
function KanbanCard({ task, onEdit, onDelete, onComplete, onDragStart, timer, onTimer }) {
  const { t } = useTranslation();
  const overdue = isTaskOverdue(task);
  const isRunning = timer?.taskId === task.id;
  return (
    <Card
      draggable
      onDragStart={(e) => {
        e.dataTransfer.effectAllowed = 'move';
        onDragStart(task.id);
      }}
      elevation={0}
      sx={{
        mb: 1, borderRadius: 2, cursor: 'grab',
        border: '1.5px solid',
        borderColor: isRunning ? '#22c55e' : (overdue ? 'error.light' : 'divider'),
        '&:hover': { boxShadow: 3, borderColor: isRunning ? '#22c55e' : 'primary.light' },
        '&:active': { cursor: 'grabbing', opacity: 0.7 },
        transition: 'box-shadow .15s, border-color .15s, opacity .15s',
      }}
    >
      <CardContent sx={{ p: '10px 12px !important' }}>
        <Typography
          variant="body2"
          sx={{ fontWeight: 600, mb: 0.5, lineHeight: 1.3, cursor: 'pointer', '&:hover': { color: 'primary.main' } }}
          onClick={() => onEdit(task)}
        >
          {task.title}
        </Typography>
        {task.description && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.75 }}>
            {task.description.length > 60 ? task.description.slice(0, 60) + '…' : task.description}
          </Typography>
        )}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap', mt: 0.5 }}>
          <Chip
            label={t('tasks.priority.' + (task.priority || 'medium'))}
            color={PRIORITY_COLORS[task.priority] || 'default'}
            size="small" variant="outlined"
            sx={{ height: 18, fontSize: '0.62rem' }}
          />
          {overdue && (
            <Chip label={t('tasks.overdue')} color="error" size="small" sx={{ height: 18, fontSize: '0.62rem' }} />
          )}
          {task.due_date && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25, ml: 'auto' }}>
              <CalIcon sx={{ fontSize: 11, color: overdue ? 'error.main' : 'text.disabled' }} />
              <Typography variant="caption" color={overdue ? 'error' : 'text.secondary'} sx={{ fontSize: '0.65rem' }}>
                {fmtDate(task.due_date)}
              </Typography>
            </Box>
          )}
        </Box>
        {task.assigned_to_name && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.75 }}>
            <Avatar sx={{ width: 18, height: 18, fontSize: 9, bgcolor: 'primary.main' }}>
              {task.assigned_to_name[0].toUpperCase()}
            </Avatar>
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.68rem', flex: 1 }}>
              {task.assigned_to_name}
            </Typography>
          </Box>
        )}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 0.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
            <Tooltip title={isRunning ? t('tasks.stopTimer') : t('tasks.startTimer')}>
              <IconButton
                size="small"
                onClick={() => onTimer(task)}
                sx={{ p: '3px', color: isRunning ? '#22c55e' : 'text.secondary',
                  animation: isRunning ? 'pulse 1.4s ease-in-out infinite' : 'none',
                  '@keyframes pulse': { '0%,100%': { opacity: 1 }, '50%': { opacity: 0.4 } },
                }}
              >
                {isRunning ? <StopIcon sx={{ fontSize: 15 }} /> : <PlayIcon sx={{ fontSize: 15 }} />}
              </IconButton>
            </Tooltip>
            {isRunning && (
              <Typography variant="caption" sx={{ fontSize: '0.68rem', fontWeight: 700, color: '#22c55e', fontVariantNumeric: 'tabular-nums' }}>
                {fmtElapsed(timer.elapsed)}
              </Typography>
            )}
          </Box>
          <Box sx={{ display: 'flex' }}>
            {task.status !== 'completed' && (
              <Tooltip title={t('tasks.markComplete')}>
                <IconButton size="small" color="success" onClick={() => onComplete(task)} sx={{ p: '3px' }}>
                  <CheckOutlineIcon sx={{ fontSize: 15 }} />
                </IconButton>
              </Tooltip>
            )}
            <Tooltip title={t('common.edit')}>
              <IconButton size="small" color="primary" onClick={() => onEdit(task)} sx={{ p: '3px' }}>
                <EditIcon sx={{ fontSize: 14 }} />
              </IconButton>
            </Tooltip>
            <Tooltip title={t('common.delete')}>
              <IconButton size="small" color="error" onClick={() => onDelete(task.id)} sx={{ p: '3px' }}>
                <DeleteIcon sx={{ fontSize: 14 }} />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
}

/* ─── main component ─────────────────────────────────────────── */
export default function TasksBoard() {
  const { t } = useTranslation();
  const history = useHistory();
  const currentUser = globals().getLogedInUser();

  const [tasks, setTasks]             = useState([]);
  const [users, setUsers]             = useState([]);
  const [clients, setClients]         = useState([]);
  const [projects, setProjects]       = useState([]);
  const [taskActivities, setTaskActivities] = useState([]);
  const [loading, setLoading]         = useState(false);
  const [view, setView]               = useState('kanban');
  const [toast, setToast]             = useState({ open: false, message: '', variant: 'info' });
  const [openDialog, setOpenDialog]   = useState(false);
  const [editingTask, setEditingTask] = useState(null);
  const [formData, setFormData]       = useState(EMPTY_FORM);
  const [saving, setSaving]           = useState(false);
  const [dragOverCol, setDragOverCol] = useState(null);
  const dragId  = useRef(null);
  const [openActDialog, setOpenActDialog] = useState(false);
  const [actForm, setActForm] = useState({ title: '', project_id: '', day_of_month: '', start_date: '', end_date: '', is_active: true });
  const [activitySearch, setActivitySearch] = useState('');
  const [timer, setTimer]  = useState(null);
  const timerRef = useRef(null);

  useEffect(() => {
    loadTasks();
    loadUsers();
    loadClients();
    loadProjects();
    loadTaskActivities();
  }, []);

  useEffect(() => {
    if (timer) {
      timerRef.current = setInterval(() => {
        setTimer((prev) => prev ? { ...prev, elapsed: (Date.now() - prev.startedAt.getTime()) / 1000 } : null);
      }, 1000);
    } else {
      clearInterval(timerRef.current);
    }
    return () => clearInterval(timerRef.current);
  }, [timer?.taskId]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadTasks = async () => {
    try {
      setLoading(true);
      const res = await apiService().get('tasks');
      setTasks(res.data || []);
    } catch {
      setToast({ open: true, message: t('tasks.errorLoadTasks'), variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const loadUsers          = async () => { try { const r = await apiService().get('cms_users');      setUsers(r.data || []);          } catch {} };
  const loadClients        = async () => { try { const r = await apiService().get('clients');         setClients(r.data || []);        } catch {} };
  const loadProjects       = async () => { try { const r = await apiService().get('projects');        setProjects(r.data || []);       } catch {} };
  const loadTaskActivities = async () => { try { const r = await apiService().get('taskActivities'); setTaskActivities(r.data || []); } catch {} };

  const columns = STATUS_COLS.map((col) => ({ ...col, tasks: tasks.filter((t) => t.status === col.key) }));

  const openCreate = (status = 'pending') => {
    setEditingTask(null);
    setFormData({ ...EMPTY_FORM, status, planned_start: nowLocal(), planned_end: now10() });
    setActivitySearch('');
    setOpenDialog(true);
  };

  const openEdit = (task) => {
    setEditingTask(task);
    const dtLocal = (d, t) => {
      if (!d) return '';
      const dt = t ? `${String(d).slice(0, 10)}T${String(t).slice(0, 5)}` : String(d).slice(0, 10);
      return toDatetimeLocal(dt);
    };
    setFormData({
      title:         task.title       || '',
      description:   task.description || '',
      status:        task.status      || 'pending',
      priority:      task.priority    || 'medium',
      assigned_to:   task.assigned_to || '',
      client_id:     '',
      project_id:    task.project_id  || '',
      activity_id:   task.activity_id || '',
      planned_start: dtLocal(task.start_date, task.start_time) || toDatetimeLocal(task.planned_start),
      planned_end:   dtLocal(task.end_date,   task.end_time)   || toDatetimeLocal(task.planned_end),
    });
    setActivitySearch('');
    setOpenDialog(true);
  };

  const closeDialog = () => { setOpenDialog(false); setEditingTask(null); };

  const handleSave = async () => {
    if (!formData.title.trim()) {
      setToast({ open: true, message: t('tasks.titleRequired'), variant: 'error' });
      return;
    }
    try {
      setSaving(true);
      const payload = {
        title:         formData.title,
        description:   formData.description || null,
        status:        formData.status,
        priority:      formData.priority,
        assigned_to:   formData.assigned_to  || null,
        project_id:    formData.project_id   || null,
        activity_id:   formData.activity_id  || null,
        planned_start: formData.planned_start || null,
        planned_end:   formData.planned_end   || null,
      };
      if (editingTask) {
        await apiService().put('tasks', { ...payload, id: editingTask.id });
        setToast({ open: true, message: t('tasks.taskUpdated'), variant: 'success' });
      } else {
        const uid = currentUser?.data?.id || currentUser?.id;
        if (uid) payload.created_by = uid;
        await apiService().post('tasks', payload);
        setToast({ open: true, message: t('tasks.taskCreated'), variant: 'success' });
      }
      closeDialog();
      loadTasks();
    } catch {
      setToast({ open: true, message: t('tasks.errorSaveTask'), variant: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const [confirm, confirmUI] = useConfirm();


  const handleDelete = async (id) => {
    if (!(await confirm({ title: 'Fshi detyrën', message: t('tasks.confirmDelete'), confirmLabel: 'Fshi' }))) return;
    try {
      await apiService().delete('tasks', id);
      setToast({ open: true, message: t('tasks.taskDeleted'), variant: 'success' });
      loadTasks();
    } catch {
      setToast({ open: true, message: t('tasks.errorDeleteTask'), variant: 'error' });
    }
  };

  const handleComplete = async (task) => {
    try {
      await apiService().put('tasks', { ...task, status: 'completed' });
      setToast({ open: true, message: t('tasks.markedComplete'), variant: 'success' });
      loadTasks();
    } catch {
      setToast({ open: true, message: t('common.error'), variant: 'error' });
    }
  };

  const handleTimerToggle = async (task) => {
    if (timer?.taskId === task.id) {
      const seconds = Math.round((Date.now() - timer.startedAt.getTime()) / 1000);
      setTimer(null);
      if (seconds < 2) return;
      const uid = currentUser?.data?.id || currentUser?.id || null;
      const now = new Date();
      const toHHMM = (d) => `${String(d.getHours()).padStart(2,'0')}:${String(d.getMinutes()).padStart(2,'0')}:${String(d.getSeconds()).padStart(2,'0')}`;
      try {
        await apiService().post('timeEntries', {
          worker_id:        uid,
          task_id:          task.id,
          date:             timer.startedAt.toISOString().slice(0, 10),
          start_time:       toHHMM(timer.startedAt),
          end_time:         toHHMM(now),
          duration_seconds: seconds,
          description:      task.title,
          billable:         false,
        });
      } catch {
          setToast({ open: true, message: t('tasks.errorTimerSave'), variant: 'error' });
      }
    } else {
      setTimer({ taskId: task.id, startedAt: new Date(), elapsed: 0 });
    }
  };

  const handleDrop = async (targetStatus) => {
    const id = dragId.current;
    dragId.current = null;
    if (!id) return;
    const task = tasks.find((t) => t.id === id);
    if (!task || task.status === targetStatus) return;
    setTasks((prev) => prev.map((t) => t.id === id ? { ...t, status: targetStatus } : t));
    try {
      await apiService().put('tasks', { ...task, status: targetStatus });
    } catch {
      setToast({ open: true, message: t('tasks.errorStatusChange'), variant: 'error' });
      loadTasks();
      return;
    }
    if (targetStatus === 'in_progress') {
      setTimer({ taskId: id, startedAt: new Date(), elapsed: 0 });
    }
    if (targetStatus !== 'in_progress' && timer?.taskId === id) {
      const seconds = Math.round((Date.now() - timer.startedAt.getTime()) / 1000);
      setTimer(null);
      if (seconds >= 2) {
        const uid = currentUser?.data?.id || currentUser?.id || null;
        const toHHMM = (d) => `${String(d.getHours()).padStart(2,'0')}:${String(d.getMinutes()).padStart(2,'0')}:${String(d.getSeconds()).padStart(2,'0')}`;
        try {
          await apiService().post('timeEntries', {
            worker_id: uid, task_id: id,
            date: timer.startedAt.toISOString().slice(0, 10),
            start_time: toHHMM(timer.startedAt),
            end_time: toHHMM(new Date()),
            duration_seconds: seconds,
            description: task.title,
            billable: false,
          });
        } catch {
          setToast({ open: true, message: t('tasks.errorTimerSave'), variant: 'error' });
        }
      }
    }
  };

  const set = (k) => (e) => setFormData((p) => ({ ...p, [k]: e.target.value }));

  return (
    <Box>
      {confirmUI}
      <Toast {...toast} />

      {/* ── Timer Timeline ── */}
      {timer && (
        <TimerTimeline
          timer={timer}
          task={tasks.find((t) => t.id === timer.taskId)}
          onStop={handleTimerToggle}
        />
      )}

      {/* ── Header ── */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3, flexWrap: 'wrap', gap: 1, mt: timer ? 2 : 0 }}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 700, mb: 0.25 }}>{t('tasks.boardTitle')}</Typography>
          <Typography variant="body2" color="text.secondary">
            {t('tasks.boardSubtitle')}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          <ToggleButtonGroup
            value={view} exclusive
            onChange={(_, v) => v && setView(v)}
            size="small"
            sx={{ bgcolor: 'background.paper', border: '1px solid', borderColor: 'divider', borderRadius: 2 }}
          >
            <ToggleButton value="kanban" sx={{ px: 1.5, py: 0.5, border: 'none', borderRadius: '8px !important', gap: 0.5 }}>
              <KanbanIcon sx={{ fontSize: 16 }} />
              <Typography variant="caption" sx={{ fontWeight: 600, textTransform: 'none' }}>Kanban</Typography>
            </ToggleButton>
            <ToggleButton value="list" sx={{ px: 1.5, py: 0.5, border: 'none', borderRadius: '8px !important', gap: 0.5 }}>
              <ListIcon sx={{ fontSize: 16 }} />
              <Typography variant="caption" sx={{ fontWeight: 600, textTransform: 'none' }}>{t('tasks.listView')}</Typography>
            </ToggleButton>
          </ToggleButtonGroup>
          <Button
            variant="contained" size="small" startIcon={<AddIcon />}
            onClick={openCreate}
            sx={{ textTransform: 'none', borderRadius: 2, fontWeight: 600 }}
          >
            {t('tasks.newTask')}
          </Button>
        </Box>
      </Box>

      {loading && <LinearProgress sx={{ mb: 2, borderRadius: 1 }} />}

      {/* ── Kanban view ── */}
      {view === 'kanban' && (
        <Box sx={{ overflowX: 'auto', pb: 1 }}>
          <Box sx={{ display: 'flex', gap: 2, minWidth: { xs: 700, md: 'auto' } }}>
            {columns.map((col) => (
              <Box
                key={col.key}
                onDragOver={(e) => { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; setDragOverCol(col.key); }}
                onDragLeave={(e) => { if (!e.currentTarget.contains(e.relatedTarget)) setDragOverCol(null); }}
                onDrop={(e) => { e.preventDefault(); setDragOverCol(null); handleDrop(col.key); }}
                sx={{
                  flex: 1, minWidth: 0, borderRadius: 2, p: '3px',
                  outline: dragOverCol === col.key ? `2px dashed ${col.color}` : '2px dashed transparent',
                  transition: 'outline-color .12s',
                }}
              >
                <Box sx={{
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  px: 1.5, py: 1, mb: 1.5, borderRadius: 2,
                  bgcolor: dragOverCol === col.key ? col.border : col.bg,
                  border: `1px solid ${col.border}`,
                  transition: 'background-color .12s',
                }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box sx={{ width: 28, height: 28, borderRadius: 1, bgcolor: col.color + '18', border: `1.5px solid ${col.border}`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.85rem', lineHeight: 1 }}>
                      {col.icon}
                    </Box>
                    <Typography variant="caption" sx={{ fontWeight: 800, color: col.color, textTransform: 'uppercase', letterSpacing: '.06em', fontSize: '0.68rem' }}>
                      {t('tasks.status.' + col.key)}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Box sx={{ minWidth: 22, height: 22, borderRadius: 1, bgcolor: col.color, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <Typography sx={{ color: '#fff', fontWeight: 700, fontSize: '0.7rem', lineHeight: 1, px: '4px' }}>{col.tasks.length}</Typography>
                    </Box>
                    <Tooltip title={t('tasks.addToCol', { col: t('tasks.status.' + col.key) })}>
                      <IconButton size="small" sx={{ p: '2px', color: col.color }} onClick={() => openCreate(col.key)}>
                        <AddIcon sx={{ fontSize: 14 }} />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </Box>
                {col.tasks.length === 0 ? (
                  <Box
                    sx={{ border: '1.5px dashed', borderColor: 'divider', borderRadius: 2, py: 3, textAlign: 'center', cursor: 'pointer', '&:hover': { borderColor: col.color } }}
                    onClick={() => openCreate(col.key)}
                  >
                    <Typography variant="caption" color="text.disabled">{t('tasks.addTask')}</Typography>
                  </Box>
                ) : (
                  col.tasks.map((task) => (
                    <KanbanCard
                      key={task.id}
                      task={task}
                      onEdit={openEdit}
                      onDelete={handleDelete}
                      onComplete={handleComplete}
                      onDragStart={(id) => { dragId.current = id; }}
                      timer={timer}
                      onTimer={handleTimerToggle}
                    />
                  ))
                )}
              </Box>
            ))}
          </Box>
        </Box>
      )}

      {/* ── List view ── */}
      {view === 'list' && (
        <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
          <CardContent sx={{ p: 0, '&:last-child': { pb: 0 } }}>
            <Box sx={{ px: 2.5, py: 1.75, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Typography variant="body1" sx={{ fontWeight: 700 }}>{t('tasks.allTasks')}</Typography>
              <Tooltip title={t('tasks.goToFullList')}>
                <IconButton size="small" onClick={() => history.push('/tasks')}><ArrowForwardIcon sx={{ fontSize: 18 }} /></IconButton>
              </Tooltip>
            </Box>
            <Divider />
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: 'grey.50' }}>
                    <TableCell sx={{ fontWeight: 700, fontSize: '0.78rem', py: 1.25 }}>{t('tasks.colTask')}</TableCell>
                    <TableCell sx={{ fontWeight: 700, fontSize: '0.78rem', py: 1.25, display: { xs: 'none', sm: 'table-cell' } }}>{t('tasks.colStatus')}</TableCell>
                    <TableCell sx={{ fontWeight: 700, fontSize: '0.78rem', py: 1.25, display: { xs: 'none', md: 'table-cell' } }}>{t('tasks.colPriority')}</TableCell>
                    <TableCell sx={{ fontWeight: 700, fontSize: '0.78rem', py: 1.25, display: { xs: 'none', md: 'table-cell' } }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}><PersonIcon sx={{ fontSize: 14 }} />{t('tasks.colAssigned')}</Box>
                    </TableCell>
                    <TableCell sx={{ fontWeight: 700, fontSize: '0.78rem', py: 1.25, display: { xs: 'none', sm: 'table-cell' } }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}><CalIcon sx={{ fontSize: 14 }} />{t('tasks.colDue')}</Box>
                    </TableCell>
                    <TableCell sx={{ fontWeight: 700, fontSize: '0.78rem', py: 1.25 }} align="right">{t('tasks.colActions')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {tasks.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} align="center" sx={{ py: 5 }}>
                        <TaskIcon sx={{ fontSize: 40, color: 'text.disabled', mb: 1 }} />
                        <Typography color="text.secondary" variant="body2">{t('tasks.noTasks')}</Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    tasks.map((task) => {
                      const over = isTaskOverdue(task);
                      return (
                        <TableRow
                          key={task.id} hover
                          sx={{ '&:hover': { bgcolor: 'action.hover' }, borderLeft: over ? '3px solid #ef4444' : '3px solid transparent' }}
                        >
                          <TableCell sx={{ py: 1, cursor: 'pointer' }} onClick={() => openEdit(task)}>
                            <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '0.83rem' }}>{task.title}</Typography>
                            {task.description && (
                              <Typography variant="caption" color="text.secondary" sx={{ display: { xs: 'none', sm: 'block' } }}>
                                {task.description.slice(0, 55)}{task.description.length > 55 ? '…' : ''}
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell sx={{ py: 1, display: { xs: 'none', sm: 'table-cell' } }}>
                            <Chip label={t('tasks.status.' + task.status)} color={STATUS_COLORS[task.status] || 'default'} size="small" />
                          </TableCell>
                          <TableCell sx={{ py: 1, display: { xs: 'none', md: 'table-cell' } }}>
                            <Chip label={t('tasks.priority.' + (task.priority || 'medium'))} color={PRIORITY_COLORS[task.priority] || 'default'} size="small" variant="outlined" />
                          </TableCell>
                          <TableCell sx={{ py: 1, display: { xs: 'none', md: 'table-cell' } }}>
                            {task.assigned_to_name ? (
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                                <Avatar sx={{ width: 22, height: 22, fontSize: 10, bgcolor: 'primary.main' }}>
                                  {task.assigned_to_name[0].toUpperCase()}
                                </Avatar>
                                <Typography variant="caption">{task.assigned_to_name}</Typography>
                              </Box>
                            ) : (
                              <Typography variant="caption" color="text.disabled">{t('tasks.unassigned')}</Typography>
                            )}
                          </TableCell>
                          <TableCell sx={{ py: 1, display: { xs: 'none', sm: 'table-cell' } }}>
                            <Typography variant="caption" color={over ? 'error' : 'text.secondary'} sx={{ fontWeight: over ? 600 : 400 }}>
                              {fmtDate(task.due_date)}
                            </Typography>
                          </TableCell>
                          <TableCell align="right" sx={{ py: 1, whiteSpace: 'nowrap' }}>
                            <Tooltip title={timer?.taskId === task.id ? t('tasks.stopTimer') : t('tasks.startTimer')}>
                              <IconButton
                                size="small"
                                onClick={() => handleTimerToggle(task)}
                                sx={{
                                  color: timer?.taskId === task.id ? '#22c55e' : 'text.secondary',
                                  animation: timer?.taskId === task.id ? 'pulse 1.4s ease-in-out infinite' : 'none',
                                  '@keyframes pulse': { '0%,100%': { opacity: 1 }, '50%': { opacity: 0.4 } },
                                }}
                              >
                                {timer?.taskId === task.id ? <StopIcon sx={{ fontSize: 16 }} /> : <PlayIcon sx={{ fontSize: 16 }} />}
                              </IconButton>
                            </Tooltip>
                            {timer?.taskId === task.id && (
                              <Typography component="span" variant="caption" sx={{ fontWeight: 700, color: '#22c55e', mr: 0.5, fontVariantNumeric: 'tabular-nums' }}>
                                {fmtElapsed(timer.elapsed)}
                              </Typography>
                            )}
                            {task.status !== 'completed' && (
                              <Tooltip title={t('tasks.markComplete')}>
                                <IconButton size="small" color="success" onClick={() => handleComplete(task)}>
                                  <CheckOutlineIcon sx={{ fontSize: 16 }} />
                                </IconButton>
                              </Tooltip>
                            )}
                            <Tooltip title={t('common.edit')}>
                              <IconButton size="small" color="primary" onClick={() => openEdit(task)}>
                                <EditIcon sx={{ fontSize: 16 }} />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title={t('common.delete')}>
                              <IconButton size="small" color="error" onClick={() => handleDelete(task.id)}>
                                <DeleteIcon sx={{ fontSize: 16 }} />
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      )}

      {/* ── Create / Edit Task Dialog ── */}
      <Dialog open={openDialog} onClose={closeDialog} maxWidth="sm" fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ pb: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Box sx={{ width: 44, height: 44, borderRadius: 2, bgcolor: 'primary.main', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <EditNoteIcon sx={{ color: '#fff', fontSize: 22 }} />
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
                {editingTask ? t('tasks.editTask') : t('tasks.createTask')}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {t('tasks.taskDetails')}
              </Typography>
            </Box>
            <IconButton size="small" onClick={closeDialog}
              sx={{ border: '1.5px solid', borderColor: 'divider', borderRadius: 1.5 }}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ pt: 2.5 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <FormControl fullWidth>
                <InputLabel>{t('tasks.client')}</InputLabel>
                <Select value={formData.client_id} label={t('tasks.client')}
                  onChange={(e) => setFormData((p) => ({ ...p, client_id: e.target.value, project_id: '' }))}>
                  <MenuItem key="none-cl" value=""><em>{t('tasks.selectClient')}</em></MenuItem>
                  {clients.map((c) => (
                    <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl fullWidth>
                <InputLabel>{t('tasks.linkedProject')}</InputLabel>
                <Select value={formData.project_id} label={t('tasks.linkedProject')} onChange={set('project_id')}>
                  <MenuItem key="none-pr" value=""><em>{t('tasks.noProject')}</em></MenuItem>
                  {projects
                    .filter((p) => !formData.client_id || String(p.ClientId || p.client_id) === String(formData.client_id))
                    .map((p) => (
                      <MenuItem key={p.id} value={p.id}>{p.name || p.Name}</MenuItem>
                    ))}
                </Select>
              </FormControl>
            </Box>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <FormControl fullWidth>
                <InputLabel>{t('tasks.assignedTo')}</InputLabel>
                <Select value={formData.assigned_to} label={t('tasks.assignedTo')} onChange={set('assigned_to')}>
                  <MenuItem key="none-u" value=""><em>{t('tasks.none')}</em></MenuItem>
                  {users.map((u) => (
                    <MenuItem key={u.id} value={u.id}>{u.name || u.user_name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Box sx={{ flex: 1, position: 'relative' }}>
                <TextField
                  fullWidth label={t('tasks.periodicActivity')}
                  placeholder={t('tasks.searchActivity')}
                  value={activitySearch || (
                    formData.activity_id
                      ? (taskActivities.find((a) => a.id === Number(formData.activity_id))?.title || '')
                      : ''
                  )}
                  onChange={(e) => {
                    setActivitySearch(e.target.value);
                    if (!e.target.value) setFormData((p) => ({ ...p, activity_id: '' }));
                  }}
                  InputProps={{
                    endAdornment: (
                      <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
                        {formData.activity_id && (
                          <IconButton size="small" onClick={() => { setFormData((p) => ({ ...p, activity_id: '' })); setActivitySearch(''); }}>
                            <CloseIcon sx={{ fontSize: 14 }} />
                          </IconButton>
                        )}
                        <IconButton size="small" title={t('tasks.createPeriodicActivity')}
                          onClick={() => { setActForm({ title: '', project_id: formData.project_id || '', day_of_month: '', start_date: '', end_date: '', is_active: true }); setOpenActDialog(true); }}>
                          <AddIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                      </Box>
                    ),
                  }}
                />
                {activitySearch.length >= 2 && (
                  <Box sx={{
                    position: 'absolute', zIndex: 1300, top: '100%', left: 0, right: 0,
                    bgcolor: 'background.paper', border: '1px solid', borderColor: 'divider',
                    borderRadius: 1.5, boxShadow: 3, maxHeight: 180, overflowY: 'auto', mt: 0.5,
                  }}>
                    {taskActivities
                      .filter((a) => a.title?.toLowerCase().includes(activitySearch.toLowerCase()))
                      .map((a) => (
                        <Box key={a.id}
                          onClick={() => { setFormData((p) => ({ ...p, activity_id: a.id })); setActivitySearch(''); }}
                          sx={{ px: 2, py: 1, cursor: 'pointer', '&:hover': { bgcolor: 'action.hover' } }}>
                          <Typography variant="body2">{a.title}</Typography>
                        </Box>
                      ))}
                    {taskActivities.filter((a) => a.title?.toLowerCase().includes(activitySearch.toLowerCase())).length === 0 && (
                      <Box sx={{ px: 2, py: 1.5 }}>
                      <Typography variant="caption" color="text.disabled">{t('tasks.noActivityFound')}</Typography>
                      </Box>
                    )}
                  </Box>
                )}
              </Box>
            </Box>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField label={t('tasks.startDate')} type="datetime-local" fullWidth InputLabelProps={{ shrink: true }}
                value={formData.planned_start} onChange={set('planned_start')} />
              <TextField label={t('tasks.dueDate')} type="datetime-local" fullWidth InputLabelProps={{ shrink: true }}
                value={formData.planned_end} onChange={set('planned_end')} />
            </Box>
            <FormControl fullWidth>
              <InputLabel>{t('tasks.priority.title')}</InputLabel>
              <Select value={formData.priority} label={t('tasks.priority.title')} onChange={set('priority')}>
                <MenuItem key="low" value="low">{t('tasks.priority.low')}</MenuItem>
                <MenuItem key="medium" value="medium">{t('tasks.priority.medium')}</MenuItem>
                <MenuItem key="high" value="high">{t('tasks.priority.high')}</MenuItem>
                <MenuItem key="urgent" value="urgent">{t('tasks.priority.urgent')}</MenuItem>
              </Select>
            </FormControl>
            <TextField label={t('tasks.titleLabel')} required fullWidth
              value={formData.title} onChange={set('title')}
              placeholder={t('tasks.titlePlaceholder')} />
            <TextField label={t('tasks.description')} fullWidth multiline rows={3}
              value={formData.description} onChange={set('description')} />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
          <Button onClick={closeDialog} disabled={saving}
            sx={{ textTransform: 'none', borderRadius: 2, px: 3, border: '1px solid', borderColor: 'divider' }}>
            {t('common.cancel')}
          </Button>
          <Button onClick={handleSave} variant="contained" disabled={saving}
            sx={{ textTransform: 'none', borderRadius: 2, fontWeight: 700, px: 3 }}>
            {saving ? t('tasks.saving') : (editingTask ? t('common.save') : t('tasks.createTask'))}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Krijo Aktivitet Periodik Dialog ── */}
      <Dialog open={openActDialog} onClose={() => setOpenActDialog(false)} maxWidth="sm" fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ pb: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Box sx={{ width: 44, height: 44, borderRadius: 2, bgcolor: 'primary.main', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <EditNoteIcon sx={{ color: '#fff', fontSize: 22 }} />
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.2 }}>{t('tasks.periodicActivityTitle')}</Typography>
              <Typography variant="caption" color="text.secondary">
                {t('tasks.periodicActivitySubtitle')}
              </Typography>
            </Box>
            <IconButton size="small" onClick={() => setOpenActDialog(false)}
              sx={{ border: '1.5px solid', borderColor: 'divider', borderRadius: 1.5 }}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ pt: 2.5 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
            <TextField label={t('tasks.activityName')} required fullWidth
              value={actForm.title}
              onChange={(e) => setActForm((p) => ({ ...p, title: e.target.value }))} />
            <Box sx={{ display: 'flex', gap: 2 }}>
              <FormControl fullWidth>
                <InputLabel>{t('tasks.project')}</InputLabel>
                <Select value={actForm.project_id} label={t('tasks.project')}
                  onChange={(e) => setActForm((p) => ({ ...p, project_id: e.target.value }))}>
                  <MenuItem key="none-ap" value=""><em>{t('tasks.none')}</em></MenuItem>
                  {projects.map((p) => (
                    <MenuItem key={p.id} value={p.id}>{p.name || p.Name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <TextField label={t('tasks.dayOfMonth')} type="number" fullWidth
                inputProps={{ min: 1, max: 31 }}
                value={actForm.day_of_month}
                onChange={(e) => setActForm((p) => ({ ...p, day_of_month: e.target.value }))} />
            </Box>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField label={t('tasks.startDate')} type="date" fullWidth InputLabelProps={{ shrink: true }}
                value={actForm.start_date}
                onChange={(e) => setActForm((p) => ({ ...p, start_date: e.target.value }))} />
              <TextField label={t('tasks.endDateOptional')} type="date" fullWidth InputLabelProps={{ shrink: true }}
                value={actForm.end_date}
                onChange={(e) => setActForm((p) => ({ ...p, end_date: e.target.value }))} />
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, cursor: 'pointer' }}
              onClick={() => setActForm((p) => ({ ...p, is_active: !p.is_active }))}>
              <Box sx={{
                width: 18, height: 18, borderRadius: 0.5, border: '2px solid',
                borderColor: actForm.is_active ? 'primary.main' : 'text.disabled',
                bgcolor: actForm.is_active ? 'primary.main' : 'transparent',
                display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
              }}>
                {actForm.is_active && <Box sx={{ width: 8, height: 6, borderLeft: '2px solid white', borderBottom: '2px solid white', transform: 'rotate(-45deg)', mt: '-2px' }} />}
              </Box>
              <Typography variant="body2">{t('common.active')}</Typography>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
          <Button onClick={() => setOpenActDialog(false)}
            sx={{ textTransform: 'none', borderRadius: 2, px: 3, border: '1px solid', borderColor: 'divider' }}>
            {t('common.cancel')}
          </Button>
          <Button variant="contained"
            sx={{ textTransform: 'none', borderRadius: 2, fontWeight: 700, px: 3 }}
            onClick={async () => {
              if (!actForm.title.trim()) return;
              try {
                const res = await apiService().post('taskActivities', {
                  title:        actForm.title,
                  project_id:   actForm.project_id || null,
                  day_of_month: actForm.day_of_month ? Number(actForm.day_of_month) : null,
                  start_date:   actForm.start_date || null,
                  end_date:     actForm.end_date   || null,
                  is_active:    actForm.is_active,
                  status:       actForm.is_active ? 'Active' : 'Inactive',
                });
                const newId = res.data?.id || res.data?.[0]?.id;
                await loadTaskActivities();
                if (newId) setFormData((p) => ({ ...p, activity_id: newId }));
                setOpenActDialog(false);
                setToast({ open: true, message: t('tasks.activityCreated'), variant: 'success' });
              } catch {
                setToast({ open: true, message: t('tasks.errorCreateActivity'), variant: 'error' });
              }
            }}>
            {t('tasks.createActivity')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
